/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
/**
 * Navigator — The Production Command Center
 *
 * The "Google Maps of the production" — find any scene, actor, character,
 * location, schedule entry, or breakdown item from one unified hub.
 *
 * Sections: Stats · Scenes · Locations · Cast · Characters · Strip Board · Schedule
 * Global filters: INT/EXT · Day/Night · Location · Character · Cast · Day · Status
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Search, Film, MapPin, Users, UserCircle, GripVertical, CalendarDays,
  ChevronDown, ChevronRight, Filter, X, ExternalLink, LayoutGrid,
  Flag, Zap, FileText,
  CheckCircle2, Circle, Tag
} from 'lucide-react';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): Badge was
// removed from JSX in a prior refactor. Restore <Badge>…</Badge> in filter
// chips OR delete this import.
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import SceneViewModal from '../components/SceneViewModal';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Design tokens ─────────────────────────────────────────────────────────────
const DT = {
  bg:        'var(--cb-bg)',
  surface:   'var(--cb-surface)',
  card:      'var(--cb-surface-h)',
  cardHover: 'var(--cb-surface-a)',
  border:    'var(--cb-border)',
  accent:    'var(--cb-accent)',
  accentDim: 'var(--cb-accent-dim)',
  textPri:   'var(--cb-text)',
  textSec:   'var(--cb-text-sec)',
  textDim:   'var(--cb-text-dim)',
};

const SECTIONS = [
  { id: 'scenes',     label: 'Scenes',      icon: Film },
  { id: 'locations',  label: 'Locations',   icon: MapPin },
  { id: 'cast',       label: 'Cast',        icon: Users },
  { id: 'characters', label: 'Characters',  icon: UserCircle },
  { id: 'stripboard', label: 'Strip Board', icon: GripVertical },
  { id: 'schedule',   label: 'Schedule',    icon: CalendarDays },
];

const SCENE_TYPE_COLORS = {
  'EXT DAY':   '#F5C518', 'INT DAY':  '#60A5FA',
  'EXT NIGHT': '#8B5CF6', 'INT NIGHT': '#34D399',
  'EXT DAWN':  '#FB923C', 'EXT DUSK': '#F97316',
};
const sceneTypeColor = (s) => {
  if (s.is_flagged) return '#F87171';
  const k = `${(s.location_type||'INT').toUpperCase()} ${(s.time_of_day||'DAY').toUpperCase()}`;
  return SCENE_TYPE_COLORS[k] || '#60A5FA';
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, value, label, color = DT.accent }) => (
  <div className="flex-1 rounded-xl p-4 flex flex-col gap-1 min-w-[110px]"
       style={{ background: DT.card, border: `1px solid ${DT.border}` }}>
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4" style={{ color }} />
      <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: DT.textDim }}>
        {label}
      </span>
    </div>
    <span className="text-2xl font-bold tabular-nums" style={{ color: DT.textPri }}>{value}</span>
  </div>
);

// ── Scene Row ─────────────────────────────────────────────────────────────────
const SceneRow = ({ scene, onOpen }) => {
  const c = sceneTypeColor(scene);
  const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);
  return (
    <button
      onClick={() => onOpen(scene)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-100 group"
      style={{ background: 'transparent' }}
      onMouseEnter={e => e.currentTarget.style.background = DT.cardHover}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      data-testid={`nav-scene-${scene.scene_number ?? 'unnumbered'}`}
    >
      <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: c }} />
      <span className="text-sm font-bold tabular-nums w-8 flex-shrink-0" style={{ color: c }}>
        {formatSceneNumber(scene.scene_number, { padStart: 2 })}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: DT.textPri, letterSpacing: 0 }}>
          {scene.scene_header}
        </p>
        {scene.synopsis && (
          <p className="text-[11px] truncate mt-0.5" style={{ color: DT.textSec, letterSpacing: 0 }}>
            {scene.synopsis}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[10px] font-mono" style={{ color: DT.textDim }}>{pages}p</span>
        {scene.is_flagged && <Flag className="h-3 w-3 text-red-400" />}
        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: DT.textDim }} />
      </div>
    </button>
  );
};

// ── Collapsible Group ─────────────────────────────────────────────────────────
const CollapsibleGroup = ({ title, subtitle, count, color, scenes, onOpen, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl overflow-hidden mb-2" style={{ border: `1px solid ${DT.border}` }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 transition-all duration-100"
        style={{ background: open ? DT.card : DT.surface }}
        onMouseEnter={e => e.currentTarget.style.background = DT.card}
        onMouseLeave={e => e.currentTarget.style.background = open ? DT.card : DT.surface}
      >
        {color && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />}
        <div className="flex-1 text-left">
          <span className="text-sm font-semibold" style={{ color: DT.textPri, letterSpacing: 0 }}>{title}</span>
          {subtitle && <span className="text-xs ml-2" style={{ color: DT.textSec }}>{subtitle}</span>}
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full mr-2"
              style={{ background: DT.accentDim, color: DT.accent }}>
          {count} {count === 1 ? 'scene' : 'scenes'}
        </span>
        {open ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" style={{ color: DT.textDim }} />
               : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" style={{ color: DT.textDim }} />}
      </button>
      {open && (
        <div className="px-2 pb-2 pt-1" style={{ background: DT.card, borderTop: `1px solid ${DT.border}` }}>
          {scenes.map(s => <SceneRow key={s.id} scene={s} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function NavigatorPage({ embedded = false, initialSection = 'scenes' } = {}) {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [breakdown, setBreakdown] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [scheduleDays, setScheduleDays] = useState([]);
  const [scriptStatus, setScriptStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, _setActiveSection] = useState(initialSection);
  // Sync to parent when embedded — read directly, no extra effect needed
  const effectiveSection = embedded ? initialSection : activeSection;
  const setActiveSection = _setActiveSection;

  // Global filters
  const [filters, setFilters] = useState({
    locType: '',    // INT | EXT
    timeOfDay: '',  // DAY | NIGHT | DAWN | DUSK
    location: '',
    character: '',
    cast: '',
    scheduledOnly: '',   // 'scheduled' | 'unscheduled'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Scene search
  const [sceneSearch, setSceneSearch] = useState('');

  const [viewingScene, setViewingScene] = useState(null); // for SceneViewModal

  // Load all data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [bdRes, charRes, schedRes, scriptRes] = await Promise.allSettled([
          axios.get(`${API}/projects/${projectId}/breakdown`, { headers }),
          axios.get(`${API}/projects/${projectId}/characters`, { headers }),
          axios.get(`${API}/projects/${projectId}/schedule`, { headers }),
          axios.get(`${API}/projects/${projectId}/script-status`, { headers }),
        ]);
        if (bdRes.status === 'fulfilled') setBreakdown(bdRes.value.data || []);
        if (charRes.status === 'fulfilled') setCharacters(charRes.value.data || []);
        if (schedRes.status === 'fulfilled') setScheduleDays(schedRes.value.data || []);
        if (scriptRes.status === 'fulfilled') setScriptStatus(scriptRes.value.data);
      } catch (e) {
        toast.error('Failed to load Navigator data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]); // eslint-disable-line

  // Scheduled scene IDs
  const scheduledIds = useMemo(() =>
    new Set(scheduleDays.flatMap(d => d.scene_ids || [])),
    [scheduleDays]
  );

  // Active filter count
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Apply global filters to scene list
  const filteredScenes = useMemo(() => {
    return breakdown.filter(s => {
      if (filters.locType && (s.location_type || 'INT').toUpperCase() !== filters.locType) return false;
      if (filters.timeOfDay && (s.time_of_day || 'DAY').toUpperCase() !== filters.timeOfDay) return false;
      if (filters.location && !(s.location_name || '').toLowerCase().includes(filters.location.toLowerCase())) return false;
      if (filters.character && !(s.characters_in_scene || []).some(c => c.toLowerCase().includes(filters.character.toLowerCase()))) return false;
      if (filters.cast && !(s.characters_in_scene || []).some(c => c.toLowerCase().includes(filters.cast.toLowerCase()))) return false;
      if (filters.scheduledOnly === 'scheduled' && !scheduledIds.has(s.id)) return false;
      if (filters.scheduledOnly === 'unscheduled' && scheduledIds.has(s.id)) return false;
      return true;
    });
  }, [breakdown, filters, scheduledIds]);

  // Scene search results
  const searchedScenes = useMemo(() => {
    if (!sceneSearch) return filteredScenes;
    const q = sceneSearch.toLowerCase();
    return filteredScenes.filter(s =>
      String(s.scene_number ?? '').includes(q) ||
      (s.scene_header || '').toLowerCase().includes(q) ||
      (s.synopsis || '').toLowerCase().includes(q) ||
      (s.location_name || '').toLowerCase().includes(q) ||
      (s.characters_in_scene || []).some(c => c.toLowerCase().includes(q))
    );
  }, [filteredScenes, sceneSearch]);

  // Location groups
  const locationGroups = useMemo(() => {
    const map = {};
    filteredScenes.forEach(s => {
      const loc = (s.location_name || 'UNKNOWN LOCATION').trim();
      const key = loc.toLowerCase();
      if (!map[key]) map[key] = { label: loc, scenes: [] };
      map[key].scenes.push(s);
    });
    return Object.values(map)
      .sort((a, b) => b.scenes.length - a.scenes.length)
      .map(g => [g.label, g.scenes]);
  }, [filteredScenes]);

  // Cast groups (from characters_in_scene) — deduped case-insensitively
  const castGroups = useMemo(() => {
    const map = {};
    filteredScenes.forEach(s => {
      (s.characters_in_scene || []).forEach(name => {
        const key = (name || '').trim().toLowerCase();
        if (!key) return;
        if (!map[key]) map[key] = { label: name.trim(), scenes: [] };
        map[key].scenes.push(s);
      });
    });
    return Object.values(map)
      .sort((a, b) => b.scenes.length - a.scenes.length)
      .map(g => [g.label, g.scenes]);
  }, [filteredScenes]);

  // Character groups (from characters collection) — deduplicated by name+role
  const characterGroups = useMemo(() => {
    const dedup = new Map();   // key: name|role
    for (const c of characters) {
      if (!c || !c.name) continue;
      const key = `${(c.name || '').trim()}|${(c.character_type || '').toUpperCase()}`;
      const prev = dedup.get(key);
      if (!prev) {
        dedup.set(key, { ...c });
      } else {
        // Merge: keep the doc with more scenes; combine scene_numbers
        const mergedScenes = Array.from(new Set([
          ...(prev.scene_numbers || []),
          ...(c.scene_numbers || []),
        ]));
        const total = Math.max(prev.total_scenes || 0, c.total_scenes || 0, mergedScenes.length);
        dedup.set(key, {
          ...prev,
          scene_numbers: mergedScenes,
          total_scenes: total,
          actor_name: prev.actor_name || c.actor_name || '',
        });
      }
    }
    return Array.from(dedup.values())
      .filter(c => c.total_scenes > 0)
      .sort((a, b) => (b.total_scenes || 0) - (a.total_scenes || 0))
      .map(char => ({
        char,
        scenes: filteredScenes.filter(s =>
          (char.scene_numbers || []).includes(s.scene_number) ||
          (s.characters_in_scene || []).some(n => n.toLowerCase() === (char.name || '').toLowerCase())
        ),
      }))
      .filter(g => g.scenes.length > 0);
  }, [characters, filteredScenes]);

  // Stats
  const stats = useMemo(() => {
    const totalPages = breakdown.reduce((sum, s) => sum + (s.page_count_eighths || 0), 0) / 8;
    const locations = new Set(breakdown.map(s => s.location_name).filter(Boolean));
    const cast = new Set(breakdown.flatMap(s => s.characters_in_scene || []));
    return {
      scenes: breakdown.length,
      pages: totalPages.toFixed(1),
      locations: locations.size,
      cast: cast.size,
      shootDays: scheduleDays.length,
      version: scriptStatus?.version_number || '—',
    };
  }, [breakdown, scheduleDays, scriptStatus]);

  const openScene = useCallback((scene) => {
    setViewingScene(scene);
  }, []);

  const clearFilters = () => setFilters({ locType:'', timeOfDay:'', location:'', character:'', cast:'', scheduledOnly:'' });

  // Unique filter options
  const allLocations = useMemo(() => [...new Set(breakdown.map(s => s.location_name).filter(Boolean))].sort(), [breakdown]);
  const allTimes     = useMemo(() => [...new Set(breakdown.map(s => (s.time_of_day||'DAY').toUpperCase()).filter(Boolean))].sort(), [breakdown]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: DT.bg }}>
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: DT.accent }} />
          <p className="text-sm" style={{ color: DT.textSec }}>Loading Navigator…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: DT.bg }} data-testid="navigator-page">

      {/* ── Top bar (hidden in embedded mode) ───────────────────────────── */}
      {!embedded && (
      <div className="sticky top-0 z-20 px-6 py-3 flex items-center justify-between"
           style={{ background: DT.surface, borderBottom: `1px solid ${DT.border}` }}>
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5" style={{ color: DT.accent }} />
          <h1 className="font-semibold text-xl" style={{ color: DT.textPri }}>Navigator</h1>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                style={{ background: DT.accentDim, color: DT.accent }}>
            Command Center
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(f => !f)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all"
            style={{
              background: activeFilterCount > 0 ? DT.accentDim : 'transparent',
              border: `1px solid ${activeFilterCount > 0 ? DT.accent + '60' : DT.border}`,
              color: activeFilterCount > 0 ? DT.accent : DT.textSec,
            }}
            data-testid="navigator-filter-toggle"
          >
            <Filter className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Filters</span>
            {activeFilterCount > 0 && (
              <span className="text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: DT.accent, color: '#000' }}>{activeFilterCount}</span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs px-2 py-1.5 rounded-lg transition-all"
                    style={{ color: DT.textDim, border: `1px solid ${DT.border}` }}>
              Clear
            </button>
          )}
        </div>
      </div>
      )}

      {/* ── Global Filters ───────────────────────────────────────────────── */}
      {showFilters && (
        <div className="px-6 py-4 flex flex-wrap gap-3"
             style={{ background: DT.surface, borderBottom: `1px solid ${DT.border}` }}>
          {/* INT / EXT */}
          <div className="flex items-center gap-1">
            {['', 'INT', 'EXT'].map(v => (
              <button key={v} onClick={() => setFilters(f => ({ ...f, locType: v }))}
                      className="text-xs px-2.5 py-1 rounded-md transition-all"
                      style={{ background: filters.locType === v ? DT.accentDim : 'transparent',
                               color: filters.locType === v ? DT.accent : DT.textSec,
                               border: `1px solid ${filters.locType === v ? DT.accent+'40' : DT.border}` }}>
                {v || 'All'}
              </button>
            ))}
          </div>
          {/* Day / Night */}
          <div className="flex items-center gap-1">
            {['', 'DAY', 'NIGHT', 'DAWN', 'DUSK'].map(v => (
              <button key={v} onClick={() => setFilters(f => ({ ...f, timeOfDay: v }))}
                      className="text-xs px-2.5 py-1 rounded-md transition-all"
                      style={{ background: filters.timeOfDay === v ? DT.accentDim : 'transparent',
                               color: filters.timeOfDay === v ? DT.accent : DT.textSec,
                               border: `1px solid ${filters.timeOfDay === v ? DT.accent+'40' : DT.border}` }}>
                {v || 'All Time'}
              </button>
            ))}
          </div>
          {/* Scheduled */}
          <div className="flex items-center gap-1">
            {[['', 'All'], ['scheduled', 'Scheduled'], ['unscheduled', 'Unscheduled']].map(([v, l]) => (
              <button key={v} onClick={() => setFilters(f => ({ ...f, scheduledOnly: v }))}
                      className="text-xs px-2.5 py-1 rounded-md transition-all"
                      style={{ background: filters.scheduledOnly === v ? DT.accentDim : 'transparent',
                               color: filters.scheduledOnly === v ? DT.accent : DT.textSec,
                               border: `1px solid ${filters.scheduledOnly === v ? DT.accent+'40' : DT.border}` }}>
                {l}
              </button>
            ))}
          </div>
          {/* Location text filter */}
          <input value={filters.location} onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}
                 placeholder="Filter by location…" className="text-xs px-3 py-1.5 rounded-lg outline-none"
                 style={{ background: DT.card, color: DT.textPri, border: `1px solid ${DT.border}`, letterSpacing: 0, minWidth: 160 }} />
          {/* Cast text filter */}
          <input value={filters.cast} onChange={e => setFilters(f => ({ ...f, cast: e.target.value }))}
                 placeholder="Filter by cast…" className="text-xs px-3 py-1.5 rounded-lg outline-none"
                 style={{ background: DT.card, color: DT.textPri, border: `1px solid ${DT.border}`, letterSpacing: 0, minWidth: 160 }} />
        </div>
      )}

      <div className="px-6 py-6 space-y-6">

        {/* ── Stats bar ────────────────────────────────────────────────────── */}
        {!embedded && (
        <div className="flex flex-wrap gap-3" data-testid="navigator-stats">
          <StatCard icon={Film}        value={stats.scenes}    label="Scenes"       />
          <StatCard icon={FileText}    value={`${stats.pages}p`} label="Script Pages" />
          <StatCard icon={MapPin}      value={stats.locations} label="Locations"    color="#60A5FA" />
          <StatCard icon={Users}       value={stats.cast}      label="Cast"         color="#34D399" />
          <StatCard icon={CalendarDays} value={stats.shootDays} label="Shoot Days"   color="#FB923C" />
          <StatCard icon={Tag}         value={`v${stats.version}`} label="Script Version" color="#8B5CF6" />
        </div>
        )}

        {/* ── Quick Actions ─────────────────────────────────────────────────── */}
        {!embedded && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Open Breakdown', icon: LayoutGrid, path: 'breakdown', color: DT.accent },
            { label: 'Strip Board',    icon: GripVertical, path: 'stripboard', color: '#60A5FA' },
            { label: 'Schedule',       icon: CalendarDays, path: 'schedule',   color: '#FB923C' },
            { label: 'Casting',        icon: Users,        path: 'dept/casting', color: '#34D399' },
          ].map(a => (
            <button key={a.path}
                    onClick={() => navigate(`/projects/${projectId}/${a.path}`)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150"
                    style={{ background: `${a.color}14`, border: `1px solid ${a.color}30`, color: a.color }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${a.color}22`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${a.color}14`; }}
            >
              <a.icon className="h-4 w-4" />
              {a.label}
            </button>
          ))}
        </div>
        )}

        {/* ── Section tabs + content ────────────────────────────────────────── */}
        <div>
          {/* Section nav (hidden in embedded mode — parent provides its own) */}
          {!embedded && (
          <div className="flex gap-1 mb-4 p-1 rounded-xl overflow-x-auto"
               style={{ background: DT.surface, border: `1px solid ${DT.border}` }}>
            {SECTIONS.map(sec => {
              const Icon = sec.icon;
              const active = effectiveSection === sec.id;
              return (
                <button key={sec.id} onClick={() => setActiveSection(sec.id)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                        style={{ background: active ? DT.accent : 'transparent', color: active ? '#000' : DT.textSec }}
                        data-testid={`nav-section-${sec.id}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {sec.label}
                </button>
              );
            })}
          </div>
          )}

          {/* ── SCENES ─────────────────────────────────────────────────────── */}
          {effectiveSection === 'scenes' && (
            <div>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: DT.textDim }} />
                <input value={sceneSearch} onChange={e => setSceneSearch(e.target.value)}
                       placeholder="Search by scene number, heading, dialogue keyword, location, or cast…"
                       className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none"
                       style={{ background: DT.card, color: DT.textPri, border: `1px solid ${DT.border}`, letterSpacing: 0 }}
                       data-testid="scene-search-input" />
                {sceneSearch && (
                  <button onClick={() => setSceneSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: DT.textDim }}>
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="text-[10px] mb-2 flex items-center gap-2" style={{ color: DT.textDim }}>
                <span>{searchedScenes.length} scene{searchedScenes.length !== 1 ? 's' : ''}</span>
                {sceneSearch && <span style={{ color: DT.accent }}>matching &ldquo;{sceneSearch}&rdquo;</span>}
              </div>
              <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${DT.border}`, background: DT.card }}>
                <div className="p-2 max-h-[60vh] overflow-y-auto">
                  {searchedScenes.length === 0
                    ? <p className="text-sm text-center py-8" style={{ color: DT.textDim }}>No scenes match your search.</p>
                    : searchedScenes.map(s => <SceneRow key={s.id} scene={s} onOpen={openScene} />)
                  }
                </div>
              </div>
            </div>
          )}

          {/* ── LOCATIONS ──────────────────────────────────────────────────── */}
          {effectiveSection === 'locations' && (
            <div>
              <p className="text-xs mb-3" style={{ color: DT.textDim }}>
                {locationGroups.length} unique locations · {filteredScenes.length} scenes
              </p>
              <div className="max-h-[65vh] overflow-y-auto pr-1">
                {locationGroups.map(([loc, scenes], i) => (
                  <CollapsibleGroup key={loc} title={loc} count={scenes.length}
                                    color={sceneTypeColor(scenes[0])} scenes={scenes}
                                    onOpen={openScene} defaultOpen={i === 0} />
                ))}
              </div>
            </div>
          )}

          {/* ── CAST ───────────────────────────────────────────────────────── */}
          {effectiveSection === 'cast' && (
            <div>
              <p className="text-xs mb-3" style={{ color: DT.textDim }}>
                {castGroups.length} cast members across {filteredScenes.length} scenes
              </p>
              <div className="max-h-[65vh] overflow-y-auto pr-1">
                {castGroups.map(([name, scenes]) => (
                  <CollapsibleGroup key={name} title={name}
                                    subtitle={`${scenes.length} appearance${scenes.length !== 1 ? 's' : ''}`}
                                    count={scenes.length} color={DT.accent}
                                    scenes={scenes} onOpen={openScene} />
                ))}
              </div>
            </div>
          )}

          {/* ── CHARACTERS ─────────────────────────────────────────────────── */}
          {effectiveSection === 'characters' && (
            <div>
              <p className="text-xs mb-3" style={{ color: DT.textDim }}>
                {characterGroups.length} character{characterGroups.length === 1 ? '' : 's'} in script
              </p>
              <div className="max-h-[65vh] overflow-y-auto pr-1">
                {characterGroups.length > 0
                  ? characterGroups.map(({ char, scenes }) => (
                    <CollapsibleGroup key={char.id || char.name}
                                      title={`${char.name}${char.actor_name ? ` (${char.actor_name})` : ''}`}
                                      subtitle={char.character_type || ''}
                                      count={scenes.length}
                                      color={char.character_type === 'LEAD' ? DT.accent : '#60A5FA'}
                                      scenes={scenes} onOpen={openScene} />
                  ))
                  : characters.map(char => (
                    <div key={char.id || char.name} className="flex items-center gap-3 px-4 py-3 rounded-xl mb-2"
                         style={{ background: DT.card, border: `1px solid ${DT.border}` }}>
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold"
                           style={{ background: DT.accentDim, color: DT.accent }}>
                        {(char.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: DT.textPri, letterSpacing: 0 }}>
                          {char.name}{char.actor_name ? ` (${char.actor_name})` : ''}
                        </p>
                        <p className="text-[10px]" style={{ color: DT.textDim }}>
                          {char.character_type} · {char.total_scenes} scenes
                        </p>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {/* ── STRIP BOARD ────────────────────────────────────────────────── */}
          {effectiveSection === 'stripboard' && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {/* Scheduled */}
                <div className="flex-1 rounded-xl overflow-hidden" style={{ border: `1px solid ${DT.border}` }}>
                  <div className="px-4 py-3 flex items-center gap-2"
                       style={{ background: 'rgba(52,211,153,0.1)', borderBottom: `1px solid ${DT.border}` }}>
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-semibold text-emerald-400">Scheduled</span>
                    <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(52,211,153,0.15)', color: '#34D399' }}>
                      {filteredScenes.filter(s => scheduledIds.has(s.id)).length}
                    </span>
                  </div>
                  <div className="p-2 max-h-72 overflow-y-auto" style={{ background: DT.card }}>
                    {filteredScenes.filter(s => scheduledIds.has(s.id)).map(s => (
                      <SceneRow key={s.id} scene={s} onOpen={openScene} />
                    ))}
                  </div>
                </div>
                {/* Unscheduled */}
                <div className="flex-1 rounded-xl overflow-hidden" style={{ border: `1px solid ${DT.border}` }}>
                  <div className="px-4 py-3 flex items-center gap-2"
                       style={{ background: 'rgba(232,197,71,0.08)', borderBottom: `1px solid ${DT.border}` }}>
                    <Circle className="h-4 w-4" style={{ color: DT.accent }} />
                    <span className="text-sm font-semibold" style={{ color: DT.accent }}>Unscheduled</span>
                    <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: DT.accentDim, color: DT.accent }}>
                      {filteredScenes.filter(s => !scheduledIds.has(s.id)).length}
                    </span>
                  </div>
                  <div className="p-2 max-h-72 overflow-y-auto" style={{ background: DT.card }}>
                    {filteredScenes.filter(s => !scheduledIds.has(s.id)).map(s => (
                      <SceneRow key={s.id} scene={s} onOpen={openScene} />
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={() => navigate(`/projects/${projectId}/stripboard`)}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all"
                      style={{ background: DT.accentDim, color: DT.accent, border: `1px solid rgba(232,197,71,0.2)` }}>
                <GripVertical className="h-4 w-4" />
                Open Full Strip Board
              </button>
            </div>
          )}

          {/* ── SCHEDULE ───────────────────────────────────────────────────── */}
          {effectiveSection === 'schedule' && (
            <div className="space-y-3">
              {scheduleDays.length === 0
                ? <div className="text-center py-12" style={{ color: DT.textDim }}>
                    <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No shoot days scheduled yet.</p>
                    <button onClick={() => navigate(`/projects/${projectId}/schedule`)}
                            className="mt-3 text-xs px-3 py-1.5 rounded-lg"
                            style={{ background: DT.accentDim, color: DT.accent }}>
                      Open Schedule
                    </button>
                  </div>
                : scheduleDays.map(day => {
                    const dayScenes = (day.scene_ids || [])
                      .map(id => breakdown.find(s => s.id === id)).filter(Boolean);
                    const totalPages = dayScenes.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0) / 8;
                    const castSet = new Set(dayScenes.flatMap(s => s.characters_in_scene || []));
                    return (
                      <CollapsibleGroup key={day.id}
                                        title={`Day ${day.day_number}${day.shoot_date ? ` · ${day.shoot_date}` : ''}`}
                                        subtitle={day.location || ''}
                                        count={dayScenes.length}
                                        color={day.day_color || DT.accent}
                                        scenes={dayScenes}
                                        onOpen={openScene} />
                    );
                  })
              }
              {scheduleDays.length > 0 && (
                <button onClick={() => navigate(`/projects/${projectId}/schedule`)}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold mt-2"
                        style={{ background: 'rgba(251,146,60,0.1)', color: '#FB923C', border: `1px solid rgba(251,146,60,0.2)` }}>
                  <CalendarDays className="h-4 w-4" />
                  Open Full Schedule
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scene View Modal — opens Script tab by default */}
      {viewingScene && (
        <SceneViewModal
          scene={viewingScene}
          projectId={projectId}
          token={token}
          defaultTab="script"
          onClose={() => setViewingScene(null)}
        />
      )}
    </div>
  );
}
